import React, { useState, useRef } from "react";
import { useSelector } from "react-redux";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { currentSelectedLanguage } from "../../../../../StateManagement/actions";

export function ImportDomainsModal({ isOpen, onClose, onImport, existingDomains = [], onOpenValidationPopup, showAlert }) {
  const selectedLanguage = useSelector(currentSelectedLanguage);
  const fileInputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  if (!isOpen) return null;

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      setSelectedFile(file);
    }
  };

  const handleFileInputChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setSelectedFile(file);
    }
  };

  const processFile = async (fileToProcess) => {
    const file = fileToProcess || selectedFile;
    if (!file) return;

    const ext = file.name.split(".").pop().toLowerCase();
    if (!["csv", "xlsx", "xls"].includes(ext)) {
      if (showAlert) {
        showAlert(selectedLanguage?.LstLanguageCodes?.UI_IncorrectFileType, "warning");
      }
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    setIsProcessing(true);

    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: "array" });
      const firstSheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[firstSheetName];
      const rows = XLSX.utils.sheet_to_json(worksheet, { defval: "" });
      const rawHeaders = XLSX.utils.sheet_to_json(worksheet, { header: 1 })[0] || [];
      const headerStrings = Array.isArray(rawHeaders) ? rawHeaders.map((h) => String(h).trim().toLowerCase()) : [];
      const isContactsTemplate = headerStrings.includes("email") || headerStrings.includes("first name") || headerStrings.includes("last name");
      const isGroupsTemplate = headerStrings.includes("group name") || headerStrings.includes("group email") || headerStrings.includes("distribution list");
      const hasDomainHeader = headerStrings.some((h) => ["domain", "domains", "domain name", "domainname", "allowed domain", "allowed domains", "allowed_domain", "allowed_domains"].includes(h));
      const isValidDomainFile = (hasDomainHeader || headerStrings.length === 1) && !isContactsTemplate && !isGroupsTemplate;

      if (!isValidDomainFile) {
        if (showAlert) {
          showAlert(selectedLanguage?.LstLanguageCodes?.UI_PleaseEnterValidFile, "warning");
        }
        setSelectedFile(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
        setIsProcessing(false);
        return;
      }

      const extractedRawDomains = [];
      if (rows && rows.length > 0) {
        rows.forEach((row) => {
          if (typeof row === "object" && row !== null) {
            const matchKey = Object.keys(row).find((k) => k && ["domain", "domains", "domain name", "domainname",
              "allowed domain", "allowed domains", "allowed_domain", "allowed_domains"].includes(k.trim().toLowerCase()));
            if (matchKey && row[matchKey]) {
              const val = String(row[matchKey]).trim();
              if (val) extractedRawDomains.push(val);
            } else {
              Object.values(row).forEach((val) => {
                if (val && typeof val === "string") {
                  extractedRawDomains.push(val.trim());
                }
              });
            }
          }
        });
      }

      if (extractedRawDomains.length === 0) {
        const text = new TextDecoder().decode(data);
        if (text) {
          text.split(/[\s,;]+/).forEach((d) => {
            if (d.trim()) extractedRawDomains.push(d.trim());
          });
        }
      }

      const domainRegex = /^[a-z0-9.-]+\.[a-z]{2,63}$/;
      const existingDomainSet = new Set((existingDomains || []).map((d) => String(d).toLowerCase().trim()).filter(Boolean));
      const seenDomainsInFile = new Set();
      const validDomains = [];
      let duplicateFound = false;
      const emailLeanghtValidation = [];
      const longDomainEmails = [];
      const invalidDomain = [];
      let totalDomainsInFileCount = 0;

      extractedRawDomains.forEach((raw) => {
        const d = raw.toLowerCase().replace(/^@/, "").trim();
        if (!d) return;
        totalDomainsInFileCount++;
        let hasMistake = false;

        if (d.length > 255) {
          emailLeanghtValidation.push({ email: raw });
          hasMistake = true;
        } else if (d.length > 190) {
          longDomainEmails.push({ email: raw });
          hasMistake = true;
        }

        if (!domainRegex.test(d)) {
          invalidDomain.push({ email: raw });
          hasMistake = true;
        }

        if (hasMistake) return;

        if (existingDomainSet.has(d) || seenDomainsInFile.has(d)) {
          duplicateFound = true;
          return;
        }

        seenDomainsInFile.add(d);
        validDomains.push(d);
      });

      if (totalDomainsInFileCount === 0) {
        onClose();
        if (onOpenValidationPopup) {
          onOpenValidationPopup({
            missingRequiredFields: [{ value: "", issue: selectedLanguage?.LstLanguageCodes?.UI_PleaseEnterValidDomainName || "Domain name cannot be empty." }]
          });
        } else if (showAlert) {
          showAlert(selectedLanguage?.LstLanguageCodes?.UI_PleaseEnterValidDomainName || "Domain name cannot be empty.", "warning");
        }
        setSelectedFile(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
        setIsProcessing(false);
        return;
      }

      const hasAnyMistakes = emailLeanghtValidation.length > 0 || longDomainEmails.length > 0 || invalidDomain.length > 0;
      if (hasAnyMistakes) {
        onClose();
        if (onOpenValidationPopup) {
          onOpenValidationPopup({ emailLeanghtValidation, longDomainEmails, invalidDomain });
        }
        setIsProcessing(false);
        return;
      }

      onClose();
      if (onImport) {
        onImport(validDomains, duplicateFound);
      }
    } catch (err) {
      console.error("Error parsing domain file:", err);
      if (showAlert) {
        showAlert(selectedLanguage?.LstLanguageCodes?.UI_PleaseEnterValidDomainName, "warning");
      }
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDownloadSample = (format = "csv") => {
    const headers = [[selectedLanguage?.LstLanguageCodes?.UI_AllowedDomains + " (e.g. example.com)"]];
    const ws = XLSX.utils.aoa_to_sheet(headers);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Domains_Template");
    if (format === "xlsx") {
      const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      const blob = new Blob([wbout], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      });
      saveAs(blob, "Domains_Template.xlsx");
    } else {
      const wbout = XLSX.write(wb, { bookType: "csv", type: "array" });
      const blob = new Blob([wbout], { type: "text/csv;charset=utf-8;" });
      saveAs(blob, "Domains_Template.csv");
    }
  };

  const handleConfirmImport = () => {
    if (selectedFile) {
      processFile(selectedFile);
    }
  };

  return (
    <div className="modalCus contacts-modal-show contacts-modal-wrapper">
      <div className="modal-dialog modal-dialog-centered contacts-modal-dialog-import">
        <div className="modal-content border-r4">
          {/* Header */}
          <div className="modal-header d-flex align-items-center justify-content-between">
            <h4 className="mb-0 text-danger fw-bold font-20">
              {selectedLanguage?.LstLanguageCodes?.UI_ImportDomains || "Import Domains"}
            </h4>
            <button type="button" className="btn btn-outline-danger Readerclose modalclose download" onClick={onClose}>
              <span className="material-icons font-20">highlight_off</span>
            </button>
          </div>

          {/* Body */}
          <div className="modal-body p-3">
            {/* Download Sample XLS / CSV Bar */}
            <div className="d-flex align-items-center mb-3">
              <span className="font-14 font-w500 text-dark">
                {selectedLanguage?.LstLanguageCodes?.UI_PleaseDownloadTheXLSorCSV}
              </span>
              <img src={"assets/img/icons/CSVdownload.png"} className="contacts-sample-download-img ms-3" alt="Download CSV" title="Download sample CSV" onClick={() => handleDownloadSample("csv")} />
              <img src={"assets/img/icons/XLSdownload.png"} className="contacts-sample-download-img ms-2 cursor-pointer" alt="Download XLS" title="Download sample XLS" onClick={() => handleDownloadSample("xlsx")} />
            </div>

            {/* Drag and Drop Zone */}
            <div className={`drag upload form-control contacts-dropzone ${dragActive ? "active-drop" : ""}`} onDragEnter={handleDrag}
              onDragLeave={handleDrag} onDragOver={handleDrag} onDrop={handleDrop} onClick={() => fileInputRef.current?.click()}>
              <div className="d-flex flex-column align-items-center justify-content-center p-3 text-center">
                <input type="file" ref={fileInputRef} className="contacts-hidden-file-input" accept=".csv,.xlsx,.xls" onChange={handleFileInputChange} />
                <span className="material-icons font-32 text-danger mb-1">
                  cloud_upload
                </span>
                <span className="font-14 font-w500 text-dark pb-1">
                  {selectedFile ? selectedFile.name : selectedLanguage?.LstLanguageCodes?.UI_DragandDropfilesHere}
                </span>
                <span className="text-muted font-12 pb-1">
                  {selectedLanguage?.LstLanguageCodes?.UI_or}
                </span>
                <span className="text-danger underline font-13 font-w600">
                  {selectedLanguage?.LstLanguageCodes?.UI_ClickAndSelectFiles}
                </span>
              </div>
            </div>

            {isProcessing && (
              <div className="text-center py-2 text-secondary font-13">
                Processing file, please wait...
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="modal-footer d-flex align-items-center justify-content-end">
            <button type="button" title={selectedLanguage?.LstLanguageCodes?.UI_Cancel || "Cancel"} className="btn btn-danger px-2 me-2 ms-1 w-140p truncate" onClick={onClose}>
              {selectedLanguage?.LstLanguageCodes?.UI_Cancel || "Cancel"}
            </button>
            <button type="button" title={selectedLanguage?.LstLanguageCodes?.UI_Import || "Import"} className="btn btn-primary px-2 ms-1 w-140p truncate" disabled={!selectedFile || isProcessing} onClick={handleConfirmImport}>
              {selectedLanguage?.LstLanguageCodes?.UI_Import || "Import"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ImportDomainsModal;